/**
 * Extra Services - Collection-Based Add-Ons for Shopify
 * Optimized for Cricketer Point theme (<product-form-component>)
 *
 * Strategy: Instead of intercepting form submit (which conflicts with the
 * theme's web component), we intercept the Add to Cart BUTTON click.
 * We stop the theme from handling it, do our own /cart/add.js call
 * with the main product + services, then redirect to cart.
 */

(function () {
  'use strict';

  const SELECTORS = {
    container: '#extra-services',
    checkbox: '.extra-services__checkbox',
    textInput: '.extra-services__text-input',
    fileInput: '.extra-services__file-input',
    radioInput: '.extra-services__radio-input',
    inputWrap: '.extra-services__input-wrap',
    radioWrap: '.extra-services__radio-wrap',
    fileWrap: '.extra-services__file-wrap',
    fileDropzone: '.extra-services__file-dropzone',
    filePreview: '.extra-services__file-preview',
    filePreviewImg: '.extra-services__file-preview-img',
    fileName: '.extra-services__file-name',
    fileRemove: '.extra-services__file-remove',
    charCurrent: '.extra-services__char-current',
    // Cricketer Point theme elements
    productForm: 'form[action*="/cart/add"]',
    addToCartBtn: '.add-to-cart-button, button[name="add"], [type="submit"][name="add"], form[action*="/cart/add"] button[type="submit"]',
    priceWrapper: 'product-price',
    priceSpan: 'product-price span.price',
    variantInput: 'input[name="id"]',
    quantityInput: 'input[name="quantity"]',
  };

  class ExtraServicesManager {
    constructor() {
      this.container = document.querySelector(SELECTORS.container);
      if (!this.container) return;

      this.checkboxes = this.container.querySelectorAll(SELECTORS.checkbox);
      this.productForm = document.querySelector(SELECTORS.productForm);
      this.priceWrapper = document.querySelector(SELECTORS.priceWrapper);
      this.priceElement = document.querySelector(SELECTORS.priceSpan);
      this.currency = this.container.dataset.currency || 'EUR';
      this.moneyFormat = this.container.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      this.basePriceCents = parseInt(this.container.dataset.basePriceCents, 10) || 0;
      this.productData = null;
      this.uploadedFiles = {};
      this._lastWrittenPrice = '';
      this._isBusy = false;

      // Capture the theme's EXACT price format from the DOM (e.g., "€{AMOUNT} EUR")
      // so our price writes match the theme perfectly — no format mismatch flickering
      this._themePriceFormat = this._captureThemePriceFormat();

      // Cache the Add to Cart button for enable/disable control
      this.addToCartBtn = this.productForm?.querySelector(SELECTORS.addToCartBtn);

      this._loadProductData();
      this._bindCheckboxes();
      this._bindTextInputs();
      this._bindFileInputs();
      this._interceptAddToCart();
      this._listenForVariantChanges();
      this._updateButtonState(); // Initial check
    }

    // ─── Price ────────────────────────────────────────────────────

    _getSelectedServices() {
      return Array.from(this.checkboxes)
        .filter(cb => cb.checked)
        .map(cb => ({
          handle: cb.dataset.serviceHandle,
          label: cb.dataset.serviceLabel,
          price: parseFloat(cb.dataset.servicePrice) || 0,
          priceCents: parseInt(cb.dataset.servicePriceCents) || 0,
          variantId: cb.dataset.serviceVariantId,
          type: cb.dataset.serviceType || 'checkbox',
        }));
    }

    _updatePriceDisplay() {
      this.priceElement = document.querySelector(SELECTORS.priceSpan);
      if (!this.priceElement) return;

      // Always get the CURRENT variant's base price from product JSON
      // This is the single source of truth — never rely on stale basePriceCents
      const variantInput = this.productForm?.querySelector(SELECTORS.variantInput);
      if (variantInput && this.productData) {
        const currentPrice = this._getVariantPrice(variantInput.value);
        if (currentPrice !== null) {
          this.basePriceCents = currentPrice;
        }
      }

      const extrasCents = this._getSelectedServices().reduce((sum, s) => sum + s.priceCents, 0);
      const totalCents = this.basePriceCents + extrasCents;
      const formatted = this._formatMoney(totalCents);

      this._lastWrittenPrice = formatted;
      this.priceElement.textContent = formatted;
    }

    async _loadProductData() {
      try {
        const path = window.location.pathname;
        const url = path.endsWith('/') ? path + 'product.js' : path + '/product.js';
        const r = await fetch(url);
        if (r.ok) this.productData = await r.json();
      } catch (e) { /* ignore */ }
    }

    _getVariantPrice(variantId) {
      if (!this.productData?.variants) return null;
      const v = this.productData.variants.find(v => v.id === parseInt(variantId, 10));
      return v ? v.price : null;
    }

    // Capture the theme's price format ONCE from the live DOM
    // e.g., "€19,99 EUR" → { template: "€{AMOUNT} EUR", usesComma: true }
    _captureThemePriceFormat() {
      const el = document.querySelector(SELECTORS.priceSpan);
      if (!el) return null;

      const text = el.textContent.trim();
      // Find the numeric part (e.g., "19,99" or "1.299,99" or "19.99")
      const match = text.match(/[\d.,]+/);
      if (!match) return null;

      const numericPart = match[0];
      return {
        template: text.replace(numericPart, '{AMOUNT}'),
        usesComma: numericPart.includes(','),
        // Check if it uses dot as thousands separator (e.g., "1.299,99")
        usesDotThousands: numericPart.includes('.') && numericPart.includes(',') && numericPart.lastIndexOf(',') > numericPart.lastIndexOf('.'),
      };
    }

    _formatMoney(cents) {
      // Use the captured theme format to produce IDENTICAL output
      if (this._themePriceFormat) {
        const amount = (cents / 100).toFixed(2);
        const parts = amount.split('.');
        let formatted;

        if (this._themePriceFormat.usesComma) {
          // European: 1.234,56
          const intPart = this._themePriceFormat.usesDotThousands
            ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            : parts[0];
          formatted = intPart + ',' + parts[1];
        } else {
          // US: 1,234.56
          formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
        }

        return this._themePriceFormat.template.replace('{AMOUNT}', formatted);
      }

      // Fallback if capture failed
      if (window.Shopify?.formatMoney) {
        try {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || window.moneyFormat || this.moneyFormat);
        } catch (e) { /* fallback below */ }
      }

      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.currency }).format(cents / 100);
    }

    // ─── Variant Change Detection ─────────────────────────────────
    //
    // Theme flow when user clicks a variant:
    //   1. variant-picker fetches new section HTML
    //   2. morph() replaces DOM elements (destroys all our observers/overrides)
    //   3. VariantUpdateEvent dispatched
    //   4. history.replaceState() updates URL to ?variant=ID
    //   5. product-price component updates the <span class="price">
    //
    // The URL ?variant=ID (step 4) is the ONLY signal that survives morph.
    // We read the variant ID from the URL, look up price from product JSON,
    // and write our price (base + services) to the DOM.
    //
    // Two checks run in parallel:
    //   A. URL poll (200ms) — detects variant change via ?variant= param
    //   B. Price text poll (200ms) — detects if theme overwrote our price after morph

    _listenForVariantChanges() {
      const self = this;

      // Get initial variant from URL or hidden input
      let lastVariantId = new URLSearchParams(window.location.search).get('variant')
                       || this.productForm?.querySelector(SELECTORS.variantInput)?.value
                       || '';

      setInterval(() => {
        const hasServices = self._getSelectedServices().length > 0;

        // ── A. Check if variant ID changed (via URL or hidden input) ──
        const urlVariant = new URLSearchParams(window.location.search).get('variant') || '';
        const inputEl = document.querySelector(SELECTORS.productForm + ' ' + SELECTORS.variantInput);
        const inputVariant = inputEl ? inputEl.value : '';
        const currentVariantId = urlVariant || inputVariant;

        if (currentVariantId && currentVariantId !== lastVariantId) {
          lastVariantId = currentVariantId;

          // Get price from product JSON (instant, reliable)
          const price = self._getVariantPrice(currentVariantId);
          if (price !== null) {
            self.basePriceCents = price;
          }

          if (hasServices) {
            // Re-apply after morph settles
            self._applyPriceAfterMorph();
          } else {
            self._lastWrittenPrice = '';
          }
          return; // Don't also check price text this cycle
        }

        // ── B. Check if theme overwrote our price (morph happened) ──
        if (!hasServices) {
          self._lastWrittenPrice = '';
          return;
        }

        const el = document.querySelector(SELECTORS.priceSpan);
        if (!el) return;

        const currentText = el.textContent.trim();
        if (currentText === self._lastWrittenPrice) return;

        // Theme wrote something different — re-apply our price
        self.priceElement = el;
        self._updatePriceDisplay();
      }, 200);
    }

    // Write our price after the theme finishes morphing.
    // Multiple attempts because morph + price component update timing varies.
    _applyPriceAfterMorph() {
      const self = this;
      [100, 250, 500, 800].forEach(delay => {
        setTimeout(() => {
          if (self._getSelectedServices().length === 0) return;
          const el = document.querySelector(SELECTORS.priceSpan);
          if (!el) return;
          self.priceElement = el;
          self._updatePriceDisplay();
        }, delay);
      });
    }

    _parsePriceText(text) {
      const cleaned = text.replace(/[^0-9.,]/g, '').trim();
      if (!cleaned) return 0;
      let normalized;
      if (cleaned.includes(',') && cleaned.includes('.')) {
        normalized = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
          ? cleaned.replace(/\./g, '').replace(',', '.')
          : cleaned.replace(/,/g, '');
      } else if (cleaned.includes(',')) {
        normalized = cleaned.replace(',', '.');
      } else {
        normalized = cleaned;
      }
      return Math.round(parseFloat(normalized) * 100) || 0;
    }

    // ─── Button State (Enable/Disable) ─────────────────────────────

    _updateButtonState() {
      if (!this.addToCartBtn) return;

      const selected = this._getSelectedServices();
      let shouldDisable = false;

      for (const svc of selected) {
        if (svc.type === 'text' || svc.type === 'number') {
          const input = this.container.querySelector(`.extra-services__text-input[data-service-handle="${svc.handle}"]`);
          if (input && !input.value.trim()) {
            shouldDisable = true;
            break;
          }
        }
        if (svc.type === 'radio') {
          const checked = this.container.querySelector(`input.extra-services__radio-input[data-service-handle="${svc.handle}"]:checked`);
          if (!checked) {
            shouldDisable = true;
            break;
          }
        }
        if (svc.type === 'file' && !this.uploadedFiles[svc.handle]) {
          shouldDisable = true;
          break;
        }
      }

      if (shouldDisable) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('extra-services--btn-disabled');
      } else {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('extra-services--btn-disabled');
      }
    }

    // ─── Checkbox Toggle ──────────────────────────────────────────

    _bindCheckboxes() {
      this.checkboxes.forEach(cb => {
        cb.addEventListener('change', () => {
          this._updatePriceDisplay();
          this._toggleSubInput(cb);
          this._updateButtonState();
        });
      });
    }

    _toggleSubInput(checkbox) {
      const item = checkbox.closest('.extra-services__item');
      const type = checkbox.dataset.serviceType;
      const selector = (type === 'text' || type === 'number') ? SELECTORS.inputWrap
        : type === 'file' ? SELECTORS.fileWrap
        : type === 'radio' ? SELECTORS.radioWrap
        : null;
      if (!selector) return;

      const wrap = item.querySelector(selector);
      if (!wrap) return;

      if (checkbox.checked) {
        wrap.style.display = '';
        wrap.classList.remove('is-hidden');
        wrap.classList.add('is-visible');
        if (type === 'text') {
          const input = wrap.querySelector(SELECTORS.textInput);
          if (input) input.focus();
        }
      } else {
        wrap.classList.remove('is-visible');
        wrap.classList.add('is-hidden');
        setTimeout(() => { wrap.style.display = 'none'; }, 150);
        if (type === 'file') this._clearFile(item, checkbox.dataset.serviceHandle);
      }
    }

    // ─── Text Inputs ──────────────────────────────────────────────

    _bindTextInputs() {
      this.container.querySelectorAll(SELECTORS.textInput).forEach(input => {
        const isNumber = input.classList.contains('extra-services__number-input');
        const counter = input.closest(SELECTORS.inputWrap)?.querySelector(SELECTORS.charCurrent);

        // Block non-digit keys on number inputs
        if (isNumber) {
          input.addEventListener('keypress', e => {
            if (!/[0-9]/.test(e.key)) e.preventDefault();
          });
          input.addEventListener('paste', e => {
            const paste = (e.clipboardData || window.clipboardData).getData('text');
            if (!/^\d+$/.test(paste)) e.preventDefault();
          });
        }

        input.addEventListener('input', () => {
          // Strip any non-digits that slipped through on number fields
          if (isNumber) input.value = input.value.replace(/\D/g, '');

          if (counter) counter.textContent = input.value.length;
          // Clear error styling on typing
          if (input.value.trim()) {
            input.classList.remove('is-error');
            const err = input.closest('.extra-services__input-wrap')?.querySelector('.extra-services__error');
            if (err) err.style.display = 'none';
          }
          // Re-evaluate button state on every keystroke
          this._updateButtonState();
        });
      });
    }

    // ─── File Inputs ──────────────────────────────────────────────

    _bindFileInputs() {
      this.container.querySelectorAll(SELECTORS.fileInput).forEach(input => {
        const item = input.closest('.extra-services__item');
        const dropzone = item.querySelector(SELECTORS.fileDropzone);
        const handle = input.dataset.serviceHandle;

        input.addEventListener('change', () => {
          if (input.files?.[0]) this._handleFile(item, handle, input.files[0]);
        });

        if (dropzone) {
          dropzone.addEventListener('dragover', e => { e.preventDefault(); dropzone.classList.add('is-dragover'); });
          dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
          dropzone.addEventListener('drop', e => {
            e.preventDefault(); dropzone.classList.remove('is-dragover');
            if (e.dataTransfer.files?.[0]) this._handleFile(item, handle, e.dataTransfer.files[0]);
          });
        }

        const removeBtn = item.querySelector(SELECTORS.fileRemove);
        if (removeBtn) removeBtn.addEventListener('click', e => { e.preventDefault(); this._clearFile(item, handle); });
      });
    }

    _handleFile(item, handle, file) {
      const dropzone = item.querySelector(SELECTORS.fileDropzone);
      const preview = item.querySelector(SELECTORS.filePreview);
      const previewImg = item.querySelector(SELECTORS.filePreviewImg);
      const fileNameEl = item.querySelector(SELECTORS.fileName);

      const reader = new FileReader();
      reader.onload = e => {
        this.uploadedFiles[handle] = { file, dataUrl: e.target.result, name: file.name };
        if (previewImg && file.type.startsWith('image/')) previewImg.src = e.target.result;
        if (fileNameEl) fileNameEl.textContent = file.name;
        if (dropzone) { dropzone.style.display = 'none'; dropzone.classList.remove('is-error'); }
        if (preview) preview.style.display = '';
        const err = item.querySelector('.extra-services__error');
        if (err) err.style.display = 'none';
        this._updateButtonState();
      };
      reader.readAsDataURL(file);
    }

    _clearFile(item, handle) {
      delete this.uploadedFiles[handle];
      const dropzone = item.querySelector(SELECTORS.fileDropzone);
      const preview = item.querySelector(SELECTORS.filePreview);
      const fileInput = item.querySelector(SELECTORS.fileInput);
      if (dropzone) dropzone.style.display = '';
      if (preview) preview.style.display = 'none';
      if (fileInput) fileInput.value = '';
      this._updateButtonState();
    }

    // ─── Add to Cart (Button Click Interception) ──────────────────

    _interceptAddToCart() {
      if (!this.productForm) return;

      // Find the Add to Cart button
      const btn = this.productForm.querySelector(SELECTORS.addToCartBtn);
      if (!btn) return;

      const self = this;

      // Use capturing phase + stopImmediatePropagation to run BEFORE the theme's handler
      btn.addEventListener('click', function (e) {
        const selected = self._getSelectedServices();

        // No services selected → let the theme handle it normally
        if (selected.length === 0) return;

        // Services selected → WE handle everything
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        // Prevent double-click
        if (self._isBusy) return;

        self._doAddToCart(btn, selected);
      }, true); // ← true = capturing phase (runs before theme's listener)

      // Also block the form submit when we're handling it
      this.productForm.addEventListener('submit', function (e) {
        if (self._isBusy) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
    }

    // ─── Add a single item with file via FormData ──────────────
    // Shopify hosts the file on CDN and stores the URL as the property value
    // The URL becomes clickable in cart and order admin

    async _addItemWithFile(variantId, properties, file, filePropertyName) {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      for (const [key, val] of Object.entries(properties)) {
        formData.append(`properties[${key}]`, val);
      }

      // Append the actual file — Shopify will host it and store the CDN URL
      formData.append(`properties[${filePropertyName}]`, file, file.name);

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.description || 'Failed to add file service');
      }
      return resp.json();
    }

    async _doAddToCart(btn, selectedServices) {
      this._isBusy = true;
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        const variantInput = this.productForm.querySelector(SELECTORS.variantInput);
        const quantityInput = this.productForm.querySelector(SELECTORS.quantityInput);
        const mainVariantId = variantInput ? variantInput.value : null;
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        if (!mainVariantId) throw new Error('No variant selected');

        const productTitle = document.querySelector('h1')?.textContent?.trim() || 'Product';

        // ── Separate services into file vs non-file ──
        const nonFileItems = [];
        const fileServices = [];
        const mainProps = {};
        const serviceLabels = [];

        for (const svc of selectedServices) {
          serviceLabels.push(svc.label);
          const hasVariant = svc.variantId && svc.variantId !== '0' && svc.variantId !== '';

          let svcValue = '';
          if (svc.type === 'text' || svc.type === 'number') {
            const input = this.container.querySelector(`.extra-services__text-input[data-service-handle="${svc.handle}"]`);
            svcValue = input?.value.trim() || '';
          }
          if (svc.type === 'radio') {
            const checked = this.container.querySelector(`input.extra-services__radio-input[data-service-handle="${svc.handle}"]:checked`);
            svcValue = checked?.value || '';
          }
          if (svc.type === 'file' && this.uploadedFiles[svc.handle]) {
            svcValue = this.uploadedFiles[svc.handle].name;
          }

          if (hasVariant) {
            const props = {
              '_service_for': mainVariantId,
              '_service_handle': svc.handle,
              'Service for': productTitle,
            };
            if ((svc.type === 'text' || svc.type === 'number') && svcValue) props[svc.label] = svcValue;
            if (svc.type === 'radio' && svcValue) props['Selected Option'] = svcValue;

            if (svc.type === 'file' && this.uploadedFiles[svc.handle]) {
              // File services must be added separately via FormData
              fileServices.push({ svc, props, file: this.uploadedFiles[svc.handle].file });
            } else {
              nonFileItems.push({ id: parseInt(svc.variantId, 10), quantity: 1, properties: props });
            }

            if (svcValue) mainProps['_' + svc.handle] = svcValue;
          } else {
            if (svc.type === 'checkbox') {
              mainProps[svc.label] = 'Yes';
            } else if (svcValue) {
              mainProps[svc.label] = svcValue;
            }
          }
        }

        if (serviceLabels.length > 0) {
          mainProps['_extra_services'] = serviceLabels.join(', ');
        }

        // ── Step 1: Add file services via FormData (one by one) ──
        // Each file gets uploaded to Shopify CDN automatically
        for (const fs of fileServices) {
          btn.textContent = 'Uploading file...';
          await this._addItemWithFile(
            parseInt(fs.svc.variantId, 10),
            fs.props,
            fs.file,
            'Uploaded File'
          );
        }

        // ── Step 2: Add non-file services + main product via JSON ──
        btn.textContent = 'Adding...';
        const items = [
          ...nonFileItems,
          {
            id: parseInt(mainVariantId, 10),
            quantity: quantity,
            properties: mainProps,
          },
        ];

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Cart error');
        }

        // Success → go to cart
        window.location.assign('/cart');

      } catch (err) {
        console.error('[Extra Services] Add to cart failed:', err.message);

        // Show a user-friendly inline error instead of alert()
        let errorEl = this.container.querySelector('.extra-services__cart-error');
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.className = 'extra-services__cart-error';
          this.container.appendChild(errorEl);
        }
        errorEl.textContent = err.message || 'Could not add to cart. Please try again.';
        errorEl.style.display = '';

        btn.disabled = false;
        btn.textContent = originalText;
        this._isBusy = false;
      }
    }
  }

  // ─── Init ──────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ExtraServicesManager());
  } else {
    new ExtraServicesManager();
  }
})();
